<template>
  <v-row no-gutters class="fill-height">
    <template v-if="isFetched">
      <v-col cols="5" class="border-right">
        <v-row no-gutters class="py-6 px-3">
          <v-col cols="auto" class="pl-3">
            <v-row>
              <span class="text-left pl-3 py-5 text-subtitle-1 font-weight-medium">{{
                $i18n.t('dialog.style.importIcon')
              }}</span>
            </v-row>
            <v-row no-gutters class="mb-5" style="flex-grow: initial">
              <v-col style="flex-grow:0">
                <v-icon color="primary">mdi-information</v-icon>
              </v-col>
              <v-col class="pl-2 text-left" style="line-height: normal">
                <span class="body-2 text-caption" style="color: DimGrey">
                  <i18n path="dialog.style.importIconSubheader">
                    <template #link>
                      <a
                        target="_blank"
                        href="https://gisbox.gis-support.com/knowledge-base/szybkie-podpowiedzi/jak-przygotowac-szablon-ikony-svg/"
                        >{{ $i18n.t('dialog.style.qgisTemplateInstruction') }}</a
                      >
                    </template>
                  </i18n>
                </span>
              </v-col>
            </v-row>
            <file-input
              class="icon-upload"
              ref="file"
              button-translation-path="button.selectFile"
              :options="dropzoneOptions"
              :show-added-files="false"
              :events="{
                'vdropzone-success': onUploadSuccess,
                'vdropzone-error': onError,
              }"
            >
            </file-input>
          </v-col>
        </v-row>
        <v-row no-gutters fill-height class="py-6 px-3 border-top">
          <v-col cols="12" class="pl-3">
            <v-row>
              <span class="text-left pl-3 py-5 text-subtitle-1 font-weight-medium">{{
                $i18n.t('dialog.style.generateIcon')
              }}</span>
            </v-row>
            <v-form v-model="isFormValid">
              <v-row no-gutters align="baseline" class="pt-2">
                <v-col cols="5" class="text-left"> {{ $i18n.t('dialog.style.template') }}: </v-col>
                <v-col cols="7" class="pr-2">
                  <data-input
                    class="style-select"
                    v-model="template"
                    @change="changeTemplate"
                    :data-type="{ name: 'select' }"
                    :items="templateIcons"
                    item-text="name"
                    item-value="name"
                    :rules="[v => !!v || this.$i18n.t('rules.required')]"
                  >
                  </data-input>
                </v-col>
              </v-row>
              <v-row no-gutters align="center" class="pt-2">
                <v-col cols="5" class="text-left"> {{ $i18n.t('dialog.style.color') }}: </v-col>
                <v-col cols="7" class="pr-2">
                  <data-input
                    class="style-select"
                    v-model="color"
                    @input="changeColor"
                    :data-type="{ name: 'color' }"
                    :rules="[v => !!v || this.$i18n.t('rules.required')]"
                  >
                  </data-input>
                </v-col>
              </v-row>
            </v-form>
            <v-row>
              <style-dialog-style-preview
                class="pa-3 d-block"
                :layer-style="templateStyle"
                geometry-type="point"
                :is-style-valid="!!newIconSource"
              />
            </v-row>
            <v-row class="pt-5 pl-3">
              <base-button
                class="mr-0"
                :disabled="!isFormValid"
                translation-path="dialog.agreeAdd"
                @click="addTemplate"
                :loading="isLoading"
              />
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="7">
        <v-row no-gutters class="pa-3">
          <v-container v-if="icons.length > 0">
            <v-row>
              <span class="text-left pl-3 pt-5 pb-2 text-subtitle-1 font-weight-medium">{{
                $i18n.t('dialog.style.icons')
              }}</span>
            </v-row>
            <v-row class="justify-start">
              <v-col v-for="(icon, idx) in icons" :key="idx" class="col-sm-12 col-md-6 col-lg-2 col-xl-2">
                <icon-tile :icon="icon" />
              </v-col>
            </v-row>
          </v-container>
          <v-container v-if="templateIcons.length > 0">
            <v-row>
              <span class="text-left pl-3 pt-5 pb-2 text-subtitle-1 font-weight-medium">{{
                $i18n.t('dialog.style.templates')
              }}</span>
            </v-row>
            <v-row class="justify-start">
              <v-col v-for="(icon, idx) in templateIcons" :key="idx" class="col-sm-12 col-md-6 col-lg-2 col-xl-2">
                <icon-tile :icon="icon" />
              </v-col>
            </v-row>
          </v-container>
        </v-row>
      </v-col>
    </template>
    <template v-else>
      <main-loader />
    </template>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import FileInput from '@/components/FileInput';
import IconTile from '@/components/IconTile';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import StyleDialogStylePreview from '@/components/StyleDialogStylePreview';

export default {
  name: 'IconsSettings',
  components: {
    FileInput,
    IconTile,
    MainLoader,
    DataInput,
    StyleDialogStylePreview,
  },
  computed: {
    icons: get('layers/icons'),
    templateIcons: get('layers/templateIcons'),
    templateStyle() {
      return {
        geometryType: 'point',
        svgSource: this.newIconSource,
      };
    },
    dropzoneOptions() {
      return {
        url: this.iconUploadURL,
        method: 'post',
        acceptedFiles: '.svg,.png',
        autoProcessQueue: true,
        uploadMultiple: false,
        addRemoveLinks: false,
        paramName: 'file',
        maxFiles: 1,
        accept: (file, done) => {
          const imgSrc = URL.createObjectURL(file);
          const img = new Image();
          img.src = imgSrc;
          const loweredName = file.name.toLowerCase();
          const self = this;
          img.onload = function() {
            if (loweredName.startsWith('gen_')) {
              done('wrongIconTemplateName');
            } else {
              if (file.type === 'image/svg+xml') {
                file.text().then(function(source) {
                  const regex = /param\((.+?)\)/gm;
                  if (source.toString().match(regex)) {
                    self.isTemplate = true;
                  } else {
                    self.isTemplate = false;
                  }
                  done();
                });
              } else {
                self.isTemplate = false;
                done();
              }
            }
          };
        },
      };
    },
  },
  data: () => ({
    isFetched: false,
    isFormValid: false,
    isLoading: false,
    isTemplate: false,
    template: null,
    color: null,
    templateSource: null,
    newIconSource: null,
  }),
  watch: {
    templateIcons: function(nV) {
      if (this.template && !nV.find(icon => icon.name === this.template)) {
        this.template = this.templateSource = this.newIconSource = null;
      }
    },
  },
  methods: {
    getIcons: call('layers/getIcons'),
    getIcon: call('layers/getIcon'),
    addIcon: call('layers/addIcon'),
    iconUploadURL() {
      return `${process.env.VUE_APP_API_URL}/icons?template=${this.isTemplate}`;
    },
    async addTemplate() {
      this.isLoading = true;
      try {
        // szukanie największego nadanego indexu
        const indexArray = this.icons
          .filter(icon => icon.name.startsWith('gen_') && icon.name.includes(this.template))
          .map(x => parseInt(x.name.split('_')[1]));
        const number = indexArray.length > 0 ? Math.max(...indexArray) + 1 : 1;
        //
        const blob = this.b64toBlob(this.newIconSource, 'image/svg+xml', `gen_${number}_${this.template}`);
        const file = new File([blob], `gen_${number}_${this.template}`, {
          type: 'image/svg+xml',
        });
        const formData = new FormData();
        formData.append('file', file);
        await this.addIcon(formData);
        this.getIconsHandler(false);
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    },
    async getIconsHandler(templates) {
      try {
        await this.getIcons({
          templates,
        });
        this.isFetched = true;
      } catch {
        this.isFetched = true;
      }
    },
    async init() {
      await this.getIconsHandler(true);
      await this.getIconsHandler(false);
    },
    onError() {
      this.$refs.file.$refs.dropzone.removeAllFiles();
    },
    onUploadSuccess() {
      this.$refs.file.$refs.dropzone.removeAllFiles();
      this.getIconsHandler(this.isTemplate);
    },
    b64toBlob(b64Data, contentType = '', name = '') {
      const blob = new Blob([b64Data], {
        type: contentType,
        name,
      });
      blob.name = name;
      return blob;
    },
    async changeTemplate() {
      const icon = await this.getIcon(this.template);
      this.templateSource = icon;
      this.changeColor();
    },
    changeColor() {
      if (this.templateSource) {
        const newTemplate = this.replaceParams(
          {
            fill: this.color,
          },
          this.templateSource
        );
        this.newIconSource = newTemplate;
      }
    },
    replaceParams(params, text) {
      const regex = /param\((.+?)\)(?: (.+?))?([;"]{1})/gm;
      text = text.replace(regex, (match, name, _default, end) => {
        let value;
        if (name in params) {
          value = params[name];
        } else if (_default) {
          value = _default;
        }
        if (!value) {
          value = '';
        }
        return value + end;
      });
      return text;
    },
  },
  mounted() {
    this.init();
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .icon-upload .dz-preview {
    display: none;
  }
  .style-preview-draw-box {
    width: 100%;
    max-width: 200px;
  }
}
.border-top {
  border-top: 1px solid rgba(32, 33, 36, 0.18);
}
.border-right {
  border-right: 1px solid rgba(32, 33, 36, 0.18);
}
</style>
